import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface ProcessStepProps {
  step: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  isActive: boolean;
  onSelect: () => void;
  isLast?: boolean;
}

const ProcessStep: React.FC<ProcessStepProps> = ({ 
  step, 
  title, 
  description, 
  icon, 
  isActive, 
  onSelect,
  isLast = false
}) => {
  return (
    <div className="relative">
      <motion.div 
        className={`flex items-center cursor-pointer group`}
        onClick={onSelect}
        whileHover={{ x: 5 }}
        initial={{ opacity: 0, x: -10 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: step * 0.1 }}
        viewport={{ once: true }}
      >
        <div className="mr-4 flex flex-col items-center">
          <motion.div 
            className={`w-12 h-12 rounded-full flex items-center justify-center z-10 border-2 ${
              isActive 
                ? 'bg-gradient-to-r from-primary to-secondary border-white/20 text-white shadow-lg shadow-primary/30' 
                : 'bg-white/10 backdrop-blur-sm border-white/10 text-white group-hover:border-primary/50 group-hover:text-primary'
            } transition-all duration-300`}
            whileHover={{ scale: 1.1 }}
            animate={isActive ? { 
              scale: 1.2,
              boxShadow: "0 0 20px rgba(227, 6, 19, 0.4)"
            } : { scale: 1 }}
            transition={{ duration: 0.5, type: "spring" }}
          >
            {icon}
          </motion.div>
          
          {!isLast && (
            <motion.div 
              className={`h-full w-0.5 ${
                isActive ? 'bg-gradient-to-b from-primary to-secondary/50' : 'bg-white/10 group-hover:bg-primary/30'
              } transition-colors mt-2`}
              initial={{ height: 0 }}
              whileInView={{ height: '100%' }}
              transition={{ duration: 0.8, delay: step * 0.1 + 0.2 }}
              viewport={{ once: true }}
            />
          )}
        </div>
        
        <div className="flex-1">
          <motion.h3 
            className={`text-lg font-semibold ${
              isActive ? 'text-white' : 'text-white/80 group-hover:text-white'
            } transition-colors`}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: step * 0.1 + 0.1 }}
            viewport={{ once: true }}
          >
            {title}
          </motion.h3>
          
          <motion.div
            className="mt-2"
            initial={{ opacity: 0, height: 0 }}
            animate={{ 
              opacity: isActive ? 1 : 0,
              height: isActive ? 'auto' : 0
            }}
            transition={{ duration: 0.3 }}
          >
            <p className="text-white/70">{description}</p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

interface IProcessStep {
  title: string;
  description: string;
  icon: React.ReactNode;
  details: {
    title: string;
    content: string;
  }[];
}

const Process: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  
  const steps: IProcessStep[] = [
    {
      title: "Initial Consultation",
      description: "We begin with a personalized consultation to understand your academic goals, interests, and career aspirations.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
      details: [
        {
          title: "Personal Assessment",
          content: "We evaluate your academic history, language proficiency, and career goals to create a tailored plan."
        },
        {
          title: "Goal Setting",
          content: "Together, we establish clear objectives for your study abroad journey in Japan."
        },
        {
          title: "Program Matching",
          content: "We identify potential universities and programs that align with your interests and qualifications."
        }
      ]
    },
    {
      title: "University Selection",
      description: "Based on your profile and preferences, we'll recommend Japanese universities that best match your academic and personal goals.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      details: [
        {
          title: "Curated Options",
          content: "We provide a shortlist of universities that match your academic profile and career aspirations."
        },
        {
          title: "Comparative Analysis",
          content: "Detailed comparison of programs, campus facilities, location advantages, and alumni outcomes."
        },
        {
          title: "Virtual Campus Tours",
          content: "Explore potential universities through virtual tours and informational sessions."
        }
      ]
    },
    {
      title: "Application Preparation",
      description: "Our team will guide you through the entire application process, including document preparation, personal statements, and recommendation letters.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      details: [
        {
          title: "Document Assistance",
          content: "Expert guidance on preparing transcripts, certificates, and other required documentation."
        },
        {
          title: "Personal Statement Review",
          content: "Professional editing and feedback on your personal statements and essays."
        },
        {
          title: "Recommendation Strategy",
          content: "Advice on selecting and approaching potential recommenders for strong letters of support."
        }
      ]
    },
    {
      title: "Language Preparation",
      description: "Enroll in our specialized Japanese language courses designed specifically for academic success in Japanese universities.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
        </svg>
      ),
      details: [
        {
          title: "Customized Language Training",
          content: "Personalized Japanese language instruction focused on academic terminology and university life."
        },
        {
          title: "JLPT Preparation",
          content: "Specialized courses to help you succeed in the Japanese Language Proficiency Test."
        },
        {
          title: "Conversation Practice",
          content: "Regular conversation sessions with native Japanese speakers to build confidence."
        }
      ]
    },
    {
      title: "Visa Application",
      description: "We provide comprehensive support for your student visa application, ensuring a smooth and successful process.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
        </svg>
      ),
      details: [
        {
          title: "Document Preparation",
          content: "Assistance with collecting and preparing all required visa documentation."
        },
        {
          title: "Application Review",
          content: "Thorough review of your visa application before submission to minimize chances of rejection."
        },
        {
          title: "Interview Coaching",
          content: "Preparation for visa interviews, including common questions and appropriate responses."
        }
      ]
    },
    {
      title: "Pre-Departure Orientation",
      description: "Before you leave, we'll provide comprehensive orientation sessions covering Japanese culture, academic expectations, and practical living tips.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      details: [
        {
          title: "Cultural Adaptation",
          content: "Workshops on Japanese cultural norms, customs, and etiquette to ease your transition."
        },
        {
          title: "Practical Matters",
          content: "Guidance on housing, banking, transportation, healthcare, and daily life in Japan."
        },
        {
          title: "Academic Expectations",
          content: "Overview of the Japanese educational system and expectations in university classrooms."
        }
      ]
    },
    {
      title: "Arrival & Settlement",
      description: "Our support continues after you arrive in Japan, with assistance for housing, bank accounts, phone services, and more.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      details: [
        {
          title: "Airport Pickup",
          content: "Coordination of transport from the airport to your accommodation."
        },
        {
          title: "Housing Assistance",
          content: "Help with move-in procedures, utility setup, and neighborhood orientation."
        },
        {
          title: "Administrative Support",
          content: "Guidance with registration processes, banking, mobile phones, and other essential services."
        }
      ]
    },
    {
      title: "Ongoing Support",
      description: "Throughout your studies in Japan, we remain available to provide academic guidance, cultural support, and career planning.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      details: [
        {
          title: "Academic Counseling",
          content: "Regular check-ins and academic support throughout your program."
        },
        {
          title: "Community Events",
          content: "Opportunities to connect with fellow international students and local Japanese communities."
        },
        {
          title: "Career Development",
          content: "Internship placements, job search assistance, and career networking events."
        }
      ]
    }
  ];
  
  return (
    <section className="py-24 relative overflow-hidden bg-slate-900 text-white">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div 
          className="absolute -top-40 -right-40 w-96 h-96 bg-primary/10 rounded-full blur-3xl"
          animate={{ 
            x: [0, 30],
            y: [0, -30]
          }}
          transition={{ 
            repeat: Infinity,
            repeatType: "reverse",
            duration: 20,
            ease: "easeInOut" 
          }}
        />
        
        <motion.div 
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"
          animate={{ 
            x: [0, -30],
            y: [0, 30]
          }}
          transition={{ 
            repeat: Infinity,
            repeatType: "reverse",
            duration: 15,
            ease: "easeInOut" 
          }}
        />
      </div>
      
      {/* Japanese pattern background */}
      <div className="absolute inset-0 overflow-hidden opacity-5 select-none pointer-events-none">
        <div className="japanese-pattern"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
        >
          <motion.span 
            className="text-sm font-medium px-4 py-1.5 rounded-full bg-white/10 text-white inline-block mb-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
          >
            Your Journey
          </motion.span>
          <h2 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-white/80 mb-6">
            The <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">Process</span>
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto text-lg">
            Our comprehensive approach ensures a smooth transition for your educational journey in Japan.
          </p>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-5 gap-10">
          <div className="md:col-span-2">
            <motion.div
              className="space-y-10"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              {steps.map((step, index) => (
                <ProcessStep
                  key={index}
                  step={index}
                  title={step.title}
                  description={step.description}
                  icon={step.icon}
                  isActive={activeStep === index}
                  onSelect={() => setActiveStep(index)}
                  isLast={index === steps.length - 1}
                />
              ))}
            </motion.div>
          </div>
          
          <div className="md:col-span-3 relative">
            <motion.div
              className="glass-dark p-8 md:p-10 rounded-2xl border border-white/5 sticky top-24 h-max"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-secondary"></div>
              
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeStep}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                >
                  <h3 className="text-2xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
                    {steps[activeStep].title}
                  </h3>
                  
                  <p className="text-white/80 mb-8">
                    {steps[activeStep].description}
                  </p>
                  
                  <div className="space-y-6">
                    {steps[activeStep].details.map((detail, index) => (
                      <motion.div 
                        key={index} 
                        className="bg-white/5 rounded-xl p-4 border border-white/10"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        whileHover={{ y: -5, backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                      >
                        <h4 className="text-lg font-semibold mb-2 text-white">
                          {detail.title}
                        </h4>
                        <p className="text-white/70 text-sm">
                          {detail.content}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                  
                  <motion.div
                    className="mt-10 flex justify-between items-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    <button
                      onClick={() => setActiveStep(prevStep => (prevStep - 1 + steps.length) % steps.length)}
                      className="px-4 py-2 rounded-lg border border-white/10 text-white/80 hover:bg-white/10 transition-colors flex items-center"
                      disabled={activeStep === 0}
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Previous
                    </button>
                    
                    <div className="flex space-x-1">
                      {steps.map((_, i) => (
                        <button
                          key={i}
                          className={`w-2 h-2 rounded-full transition-all ${
                            i === activeStep 
                              ? 'w-8 bg-gradient-to-r from-primary to-secondary' 
                              : 'bg-white/20'
                          }`}
                          onClick={() => setActiveStep(i)}
                          aria-label={`Go to step ${i + 1}`}
                        />
                      ))}
                    </div>
                    
                    <button
                      onClick={() => setActiveStep(prevStep => (prevStep + 1) % steps.length)}
                      className="px-4 py-2 rounded-lg border border-white/10 text-white/80 hover:bg-white/10 transition-colors flex items-center"
                      disabled={activeStep === steps.length - 1}
                    >
                      Next
                      <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </motion.div>
                </motion.div>
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process; 